/**
 * Shared motion tokens — single source of truth for JS-driven animation
 * (Motion / GSAP). CSS equivalents live in `globals.css`
 * (`--motion-fast/base/slow`, `--ease-standard`). See docs/04-motion.md.
 *
 * Reduced motion: client components should call `useReducedMotion` from
 * `motion/react` directly (kept out of this module so importing tokens from
 * a Server Component never pulls the client runtime into the RSC graph).
 */

/** Durations in seconds (Motion convention). */
export const durations = {
  fast: 0.15,
  base: 0.25,
  slow: 0.5,
} as const;

/** `cubic-bezier(0.4, 0, 0.2, 1)` — most transitions. */
export const easeStandard = [0.4, 0, 0.2, 1] as const;

/** Location pin bounce-in. */
export const springPin = {
  type: 'spring',
  stiffness: 500,
  damping: 15,
  mass: 0.8,
} as const;

/** Stat-circle / badge scale-in. */
export const springBadge = {
  type: 'spring',
  stiffness: 200,
  damping: 20,
} as const;

/** Sticky chapter nav pill slide lives in CSS (`StickyNav.module.css`). */

/**
 * Dashed migration-trail L→R reveal (Motion island).
 * Duration-based so Intro / State / Conclusion trails share one wall-clock pace
 * regardless of dash count. Constants in `dashedTrailReveal` re-export these.
 */
export const dashedTrail = {
  /**
   * Wall-clock span from first dash start → last dash start (seconds).
   * Shared across Intro / State / Conclusion so trails finish together
   * regardless of dash count (replaces the old fixed 1/16s stagger).
   */
  revealDurationS: 8,
  /** Per-dash opacity appear (seconds). */
  popDurationS: durations.fast,
  /** Opacity appear ease (Motion cubic-bezier). */
  popEase: easeStandard,
  /**
   * Fraction of the SVG that must intersect the viewport to start.
   * Low so the wave begins as soon as the trail enters.
   */
  inViewAmount: 0.05,
  /** Fraction for `readySelector` gates — earlier than other section reveals. */
  readyAmount: 0.2,
} as const;

/**
 * Hero → Introduction scroll scene (GSAP ScrollTrigger island).
 * Scroll distances (runway height, pull-up) live in
 * `HeroScrollScene.module.css`; these tokens tune the animation itself.
 */
export const heroScene = {
  /** Swirl circle spin across its full travel (degrees). */
  circleRotationFromDeg: -90,
  circleRotationToDeg: -160,
  /** Circle grows from this scale to 1 as it spins on. */
  circleStartScale: 0.45,
  /**
   * Extra upward drift past viewport centre, as a fraction of viewport height.
   * Keep small — large drift finishes visual cover early and the remaining
   * scrub reads as a long post-swirl hold.
   */
  circleCoverDrift: 0.04,
  /**
   * Ease for the swirl scrub on `md+`. `power1.in` keeps the circle
   * smaller/lower for most of travel so full cover lands near the end (linear
   * scrub covered ~70% through and felt like a long hold).
   */
  circleTravelEase: 'power1.in',
  /**
   * Extra pin hold after circle travel completes, as a fraction of viewport
   * height. Travel itself is derived from the runway; this only adds hold once
   * the swirl has finished.
   */
  introPinHoldViewportFraction: 0.05,
  /**
   * Narrow viewports (`HERO_SCENE_NARROW_MEDIA`, below `md`): ease-in `y` so
   * the circle bottom stays below the fold until scale covers; linear
   * scale/rotation for even growth under touch. Always boolean scrub — never
   * numeric (iOS pauses rAF during touch scroll).
   */
  narrow: {
    circleTravelEase: 'none',
    /** Ease for circle `y` only — keeps the blob planted longer than linear. */
    circleTravelYEase: 'power1.in',
  },
} as const;
